/**
 * Normalizes a ledger run's outcome into render-ready pieces, whatever wrote it:
 *  1. New pipeline (preferred): `logs` holds JSON `{ v: 1, runReport: { counts, totals, warnings } }`.
 *  2. New pipeline result strings: "job-sync: 1 created, ...", "employee-sync: ...".
 *  3. Legacy EC2 strings: "14 Jobs Updated!", "Jobs Found: 368 | Jobs Closed: 2", error dumps.
 * Returns { chips, totals, warnings, error, dryRun } — chips are zero-filtered.
 */

const CHIP_STYLES = {
	created: { color: 'green' },
	updated: { color: 'blue' },
	closed: { color: 'volcano' },
	disabled: { color: 'volcano' },
	reopened: { color: 'purple' },
	moved: { color: 'purple' },
	enrolled: { color: 'cyan' },
	unchanged: { color: 'default' },
};

const chipColor = (label) => {
	const needle = label.toLowerCase();
	const match = Object.keys(CHIP_STYLES).find((verb) => needle.includes(verb));
	return match ? CHIP_STYLES[match].color : 'blue';
};

/** camelCase report key -> human words ("usersAutoEnrolled" -> "users auto enrolled"). */
const keyToLabel = (key) =>
	key
		.replace(/([a-z\d])([A-Z])/g, '$1 $2')
		.toLowerCase()
		.replace(/\berin\b/g, 'ERIN');

const chip = (label, value) => ({
	key: label,
	label,
	value: Number(value),
	color: chipColor(label),
});

/** Collect "<number> <phrase>" chips via a regex with (value, label) captures. */
const collect = (text, regex, chips) => {
	let match;
	while ((match = regex.exec(text)) !== null) {
		chips.push(chip(match[2].trim().toLowerCase(), match[1]));
	}
};

const parseStructuredReport = (run) => {
	try {
		const parsed = JSON.parse(run.logs);
		if (!parsed || parsed.v !== 1 || !parsed.runReport) return null;
		const { counts = {}, totals = {}, warnings = [] } = parsed.runReport;
		return {
			chips: Object.entries(counts).map(([key, value]) => chip(keyToLabel(key), value)),
			totals: Object.entries(totals).map(
				([key, value]) => `${Number(value).toLocaleString()} ${keyToLabel(key)}`
			),
			warnings: [...warnings],
		};
	} catch (e) {
		return null;
	}
};

const parseResultString = (text) => {
	const chips = [];
	const totals = [];
	const warnings = [];

	// claus-platform key=value format ("mode=live incoming=28 create=1 update=27 close=0 skipped=0").
	if (/\b(create|update|close|incoming)=\d+/.test(text)) {
		const kvLabels = { create: 'created', update: 'updated', close: 'closed', skipped: 'skipped' };
		for (const [key, label] of Object.entries(kvLabels)) {
			const match = new RegExp(`\\b${key}=(\\d+)`).exec(text);
			if (match) chips.push(chip(label, match[1]));
		}
		const incoming = /\bincoming=(\d+)/.exec(text);
		if (incoming) totals.push(`${Number(incoming[1]).toLocaleString()} incoming`);
		if (/\berrors?=[1-9]/.test(text)) warnings.push('Run reported errors — see raw result');
		return { chips, totals, warnings };
	}

	// New-pipeline one-liners ("job-sync: 1 created, 0 updated, 1 closed, 13 unchanged").
	if (/^(job|employee|candidate)-sync:/.test(text)) {
		collect(text, /(\d+) (claims? created|claims? updated|users? updated|users? auto-enrolled|contacts? updated|referrals? updated|moved referrals? created|created|updated|closed|unchanged)\b/g, chips);
		const erinTotals = /(\d+) claims, (\d+) users, (\d+) contacts in ERIN/.exec(text);
		if (erinTotals) {
			totals.push(
				`${Number(erinTotals[1]).toLocaleString()} ERIN claims`,
				`${Number(erinTotals[2]).toLocaleString()} ERIN users`,
				`${Number(erinTotals[3]).toLocaleString()} ERIN contacts`
			);
		}
		const candidateTotals = /(\d+) candidate rows against (\d+) referrals/.exec(text);
		if (candidateTotals) {
			totals.push(
				`${Number(candidateTotals[1]).toLocaleString()} file rows`,
				`${Number(candidateTotals[2]).toLocaleString()} ERIN referrals`
			);
		}
	} else {
		// Legacy / pipe-delimited formats.
		collect(text, /(\d+) (Jobs? Created|Jobs? Updated|Account Claims? Created|Account Claims? Updated|Users? Created|Users? Updated|Users? Disabled|Contacts? Updated|Employees? Updated)\b/gi, chips);
		const pipeStats = /Jobs Closed:\s*(\d+)/i.exec(text);
		if (pipeStats) chips.push(chip('jobs closed', pipeStats[1]));
		const opened = /Jobs Opened:\s*(\d+)/i.exec(text);
		if (opened) chips.push(chip('jobs reopened', opened[1]));
		const pipeCreated = /Jobs Created:\s*(\d+)/i.exec(text);
		if (pipeCreated) chips.push(chip('jobs created', pipeCreated[1]));
		const found = /Jobs Found:\s*(\d+)/i.exec(text);
		if (found) totals.push(`${Number(found[1]).toLocaleString()} ERIN jobs`);
		const legacyAef = /Created:\s*(\d+)[\s\S]*?Enrolled:\s*(\d+)/.exec(text);
		if (legacyAef) {
			chips.push(chip('created', legacyAef[1]), chip('enrolled', legacyAef[2]));
		}
	}

	const truncated = /\[TRUNCATED FILE:([^\]]*)\]/.exec(text);
	if (truncated) warnings.push(`Truncated file:${truncated[1]}`);
	const suppressed = /\[SUPPRESSED:([^\]]*)\]/.exec(text);
	if (suppressed) warnings.push(`Writes suppressed by breaker:${suppressed[1]}`);
	if (/BLOCKED by breaker/i.test(text)) warnings.push('Close breaker blocked the close pass');
	if (/SAFETY GUARD/i.test(text)) warnings.push('Safety guard tripped — see raw result');
	if (/stale-replay guard/i.test(text)) warnings.push('Skipped: an older file was replayed after a newer one had completed — nothing was written');

	return { chips, totals, warnings };
};

const looksLikeError = (text) =>
	/^FAILED — /.test(text) || /StatusCodeError|^Error\b|Exception|ETIMEDOUT|ECONNREFUSED/i.test(text);

/** The pipeline writes `{v:1, error:{kind}}` (live) or `{dryRun, errorKind}`
 *  (executor) into `logs` on failure. */
const structuredErrorKind = (run) => {
	try {
		const parsed = JSON.parse(run.logs);
		return parsed?.error?.kind || parsed?.errorKind || null;
	} catch (e) {
		return null;
	}
};

/**
 * Client-side fallback for rows written before the pipeline stamped a kind
 * (and for legacy EC2 rows). Mirrors core/obs/errorKind.ts — keep the rule
 * ORDER in sync: specific buckets before the generic auth/network ones.
 */
const classifyErrorText = (text) => {
	if (/Source file no longer exists|Source file missing or unreadable|NoSuchKey|specified key does not exist/i.test(text)) return 'missing-file';
	if (/pgp|openpgp|decrypt|Message is not authenticated/i.test(text)) return 'pgp';
	if (/UnauthorizedException|not authorized|HTTP 401|HTTP 403|AccessDenied|Forbidden|credential|expired token/i.test(text)) return 'auth';
	if (/breaker|SAFETY GUARD|refusing to (reconcile|disable)|threshold/i.test(text)) return 'breaker';
	if (/ETIMEDOUT|ECONNREFUSED|ECONNRESET|EAI_AGAIN|socket hang up|ENOTFOUND|network|timed? ?out/i.test(text)) return 'network';
	if (/config|mapping|Missing required env var|secret is missing/i.test(text)) return 'config';
	if (/parse|Unexpected token|malformed|truncated/i.test(text)) return 'parse';
	return null;
};

/**
 * True when the run "completed" but the disable breaker suppressed every
 * write — matches both the structured warning ("ALL WRITES SUPPRESSED by the
 * disable breaker…") and the one synthesized from the legacy [SUPPRESSED:…]
 * result note. These runs must NOT count as successes: nothing was written.
 */
export const isWritesSuppressed = (run) =>
	parseRunSummary(run).warnings.some((warning) => /writes suppressed by/i.test(warning));

export const parseRunSummary = (run) => {
	const empty = { chips: [], totals: [], warnings: [], error: null, errorKind: null, dryRun: false };
	if (!run) return empty;

	let text = typeof run.result === 'string' ? run.result.trim() : '';
	const dryRun = /^\[DRY RUN/i.test(text) || /\bmode=dryrun\b/i.test(text);
	if (dryRun) text = text.replace(/^\[DRY RUN[^\]]*\]\s*/i, '');

	if (looksLikeError(text) || ((run.status === 'failed' || run.status === 'dryrun-failed') && text)) {
		// Collapse whitespace so multi-line dumps (JSON error bodies) read as one
		// scannable line — first-line-only turned "AppSync HTTP 401: {\n ... }"
		// into a summary that ended at the brace.
		const error = text
			.replace(/^FAILED — /, '')
			.replace(/^Dry run failed: /, '')
			.replace(/\s+/g, ' ')
			.trim()
			.slice(0, 300);
		return {
			...empty,
			dryRun,
			error,
			errorKind: structuredErrorKind(run) || classifyErrorText(error),
		};
	}

	const structured = parseStructuredReport(run);
	const parsed = structured || (text ? parseResultString(text) : null);
	if (!parsed) return { ...empty, dryRun };

	// Zero-filter: only show work that happened; all-zero runs read as "no changes".
	const nonZero = parsed.chips.filter((entry) => entry.value > 0);
	const allZero = parsed.chips.length > 0 && nonZero.length === 0;
	return {
		chips: nonZero,
		noChanges: allZero,
		totals: parsed.totals || [],
		warnings: parsed.warnings || [],
		error: null,
		dryRun,
	};
};
